import { api } from '../lib/api-client.js';

import type {
  BulkCreateInspectionsInput,
  CreateInspectionInput,
  CreateWorkOrdersFromDefectsInput,
  InspectionAnswerValue,
  InspectionAppliesTo,
  InspectionAttachmentPresignInput,
  InspectionFiltersQuery,
  InspectionPdfLayout,
  InspectionQuestionKind,
  InspectionSchema,
  InspectionStatus,
  InspectionTemplateStatus,
  SubmitInspectionInput,
  UpsertInspectionAnswerInput,
} from '@sitewise/shared';

/* ----------------------------- Types --------------------------------- */

export interface InspectionSummary {
  id: string;
  orgId: string;
  templateId: string;
  templateVersionId: string;
  buildingId: string;
  unitId: string | null;
  status: InspectionStatus;
  scheduledFor: string | null;
  startedAt: string | null;
  submittedAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  inspectorUserId: string | null;
  submittedByUserId: string | null;
  notes: string | null;
  scheduleId: string | null;
  pdfExportId: string | null;
  pdfExportEnqueuedAt: string | null;
  pdfExportFailedAt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface InspectionAnswerSummary {
  id: string;
  inspectionId: string;
  questionId: string;
  questionKind: InspectionQuestionKind;
  value: InspectionAnswerValue | null;
  notes: string | null;
  photoAttachmentIds: string[];
  isDefect: boolean;
  isDefectAcknowledged: boolean;
  defectResolvedByWorkOrderId: string | null;
  answeredAt: string | null;
  updatedAt: string;
}

export interface InspectionTemplateLite {
  id: string;
  orgId: string;
  name: string;
  description: string | null;
  appliesTo: InspectionAppliesTo;
  status: InspectionTemplateStatus;
  isArchived: boolean;
  presetSlug: string | null;
  currentVersion: number | null;
  publishedVersion: number | null;
  metadata: Record<string, unknown>;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InspectionTemplateVersionLite {
  id: string;
  templateId: string;
  version: number;
  schema: InspectionSchema;
  publishedAt: string | null;
  publishedBy: string | null;
  createdAt: string;
}

export interface InspectionDetail {
  inspection: InspectionSummary;
  template: InspectionTemplateLite;
  templateVersion: InspectionTemplateVersionLite;
  answers: InspectionAnswerSummary[];
}

export interface DefectSummary {
  answer: InspectionAnswerSummary;
  questionLabel: string;
  sectionTitle: string;
}

export interface InspectionDashboard {
  upcomingCount: number;
  defectsAwaitingWoCount: number;
  nextThree: InspectionSummary[];
}

export interface InspectionPresignResponse {
  attachmentId: string;
  uploadId: string;
  uploadUrl: string;
  objectKey: string;
  headers: Record<string, string>;
  expiresAt: string;
}

export interface InspectionPdfReady {
  status: 'ready';
  url: string;
  expiresAt: string;
  filename: string;
}

export interface InspectionPdfRendering {
  status: 'rendering';
  etaSeconds: number;
  enqueuedAt: string | null;
}

export type InspectionPdfResponse = InspectionPdfReady | InspectionPdfRendering;

/* ----------------------------- Helpers ------------------------------- */

function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length > 0) search.set(k, v.join(','));
    } else {
      search.set(k, String(v));
    }
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

/* ----------------------------- Calls --------------------------------- */

export async function listInspections(filters: Partial<InspectionFiltersQuery> = {}) {
  return api<{ inspections: InspectionSummary[] }>(
    `/inspections${qs(filters as Record<string, unknown>)}`,
  );
}

export async function fetchInspectionDashboard() {
  return api<InspectionDashboard>('/inspections/dashboard');
}

export async function fetchInspection(id: string) {
  return api<InspectionDetail>(`/inspections/${id}`);
}

export async function createInspection(input: CreateInspectionInput) {
  return api<{ inspection: InspectionSummary }>('/inspections', {
    method: 'POST',
    body: input,
  });
}

export async function bulkCreateInspections(input: BulkCreateInspectionsInput) {
  return api<{ inspections: InspectionSummary[] }>('/inspections/bulk', {
    method: 'POST',
    body: input,
  });
}

export async function startInspection(id: string) {
  return api<{ inspection: InspectionSummary }>(`/inspections/${id}/start`, {
    method: 'POST',
  });
}

export async function cancelInspection(id: string, reason?: string) {
  return api<{ inspection: InspectionSummary }>(`/inspections/${id}/cancel`, {
    method: 'POST',
    body: { reason },
  });
}

export async function upsertInspectionAnswer(
  inspectionId: string,
  questionId: string,
  input: UpsertInspectionAnswerInput,
) {
  return api<{ answer: InspectionAnswerSummary }>(
    `/inspections/${inspectionId}/answers/${questionId}`,
    { method: 'PATCH', body: input },
  );
}

export async function presignAnswerAttachment(
  inspectionId: string,
  input: InspectionAttachmentPresignInput,
) {
  return api<InspectionPresignResponse>(`/inspections/${inspectionId}/attachments/presign`, {
    method: 'POST',
    body: input,
  });
}

export async function confirmAnswerAttachment(
  inspectionId: string,
  questionId: string,
  attachmentId: string,
) {
  return api<{
    attachment: {
      id: string;
      kind: string;
      contentType: string;
      bytes: number;
      signedUrl: string | null;
    };
  }>(
    `/inspections/${inspectionId}/answers/${questionId}/attachments/${attachmentId}/confirm`,
    { method: 'POST' },
  );
}

export async function submitInspection(
  id: string,
  input: SubmitInspectionInput = { notes: null },
) {
  return api<{
    inspection: InspectionSummary;
    defects: DefectSummary[];
  }>(`/inspections/${id}/submit`, { method: 'POST', body: input });
}

export async function createWorkOrdersFromDefects(
  inspectionId: string,
  input: CreateWorkOrdersFromDefectsInput,
) {
  return api<{
    created: Array<{
      questionId: string;
      workOrder: {
        id: string;
        number: number;
        displayNumber: string;
        status: string;
        severity: string;
        category: string;
      };
    }>;
  }>(`/inspections/${inspectionId}/create-work-orders`, {
    method: 'POST',
    body: input,
  });
}

export async function acknowledgeInspectionDefect(inspectionId: string, questionId: string) {
  await api(`/inspections/${inspectionId}/acknowledge-defect`, {
    method: 'POST',
    body: { questionId },
  });
}

export async function fetchInspectionPdf(id: string, layout: InspectionPdfLayout = 'default') {
  return api<InspectionPdfResponse>(`/inspections/${id}/pdf?layout=${layout}`);
}

export async function previewInspectionPdf(id: string, layout: InspectionPdfLayout) {
  return api<InspectionPdfReady>(`/inspections/${id}/pdf/preview?layout=${layout}`);
}

export async function retryInspectionPdf(
  id: string,
  layout: InspectionPdfLayout = 'default',
) {
  return api<{ status: 'rendering' }>(`/inspections/${id}/pdf/retry?layout=${layout}`, {
    method: 'POST',
  });
}
